import type { PersistentSave } from '../../types';

const STORAGE_KEY = 'bladeworks:save:v1';

export const DEFAULT_SAVE: PersistentSave = {
  version: 1,
  renown: 0,
  unlockedWeapons: ['sword'],
  unlockedUpgrades: [],
  hallOfFame: [],
  memorial: [],
  stats: {
    totalHeroesServed: 0,
    totalHeroesSaved: 0,
    totalDeaths: 0,
    totalWeaponsForged: 0,
    totalRunsCompleted: 0,
    highestRenown: 0,
  },
};

/**
 * Wraps localStorage so the rest of the game has a stable interface.
 * Implementation can be swapped to Capacitor Preferences later without
 * touching consumers.
 */
export class SaveLoad {
  /** Returns the saved state or null if nothing exists / parse fails. */
  load(): PersistentSave | null {
    try {
      if (typeof localStorage === 'undefined') return null;
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<PersistentSave>;
      if (parsed.version !== 1) return null;
      return { ...DEFAULT_SAVE, ...parsed } as PersistentSave;
    } catch {
      return null;
    }
  }

  /** Returns the saved state, falling back to the default if absent. */
  loadOrDefault(): PersistentSave {
    return this.load() ?? structuredClone(DEFAULT_SAVE);
  }

  save(data: PersistentSave): void {
    try {
      if (typeof localStorage === 'undefined') return;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // Storage may be unavailable (private mode, quota); ignore silently.
    }
  }

  clear(): void {
    try {
      if (typeof localStorage === 'undefined') return;
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* noop */
    }
  }
}
